import useAPI, {
  useAPIMutation,
  useDistinctFilterOptions,
  useResetAPI
} from "@/services/api/hook";
import { useQueryClient } from "@tanstack/react-query";
import {
  APIObject,
  ExtendedUseQueryResult,
  Filters,
  SearchQuery
} from "@/services/api/types";
import { refreshEntityDetail, useEntities } from "@/services/api/entityhooks";
import { EntitySliceState } from "@/services/api/entitystore";
import useBoundStore, {
  MAX_ENTITIES_PER_PAGE
} from "@/services/store/BoundStore";
import { useEffect } from "react";
import { PeoplePage, PersonData, PersonJobFunctionsResponse } from "./types";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";
import { ComputedFields } from "@/services/api/types";

export const MAX_PEOPLE_PER_PAGE = MAX_ENTITIES_PER_PAGE;

export const usePeople = (
  ids: string[],
  options: {
    enabled?: boolean;
    computed_fields?: ComputedFields;
    forceRefetch?: boolean;
    include_additionals?: boolean;
  } = {}
) => {
  const forceRefetch = options.forceRefetch ?? false;
  const enabled = options.enabled;
  const include_additionals = options.include_additionals;

  const usePeopleStore = (): EntitySliceState<PersonData> => {
    return useBoundStore((state) => state.people);
  };

  return useEntities<PersonData>(
    ids,
    "people",
    usePeopleStore,
    (endpoint, params, apiOptions) =>
      useAPI(
        endpoint,
        {
          ...params,
          queryParams: {
            ...(options.computed_fields && {
              computed_fields: options.computed_fields
            }),
            ...(include_additionals && {
              include_additionals: "true"
            })
          }
        },
        {
          ...apiOptions,
          enabled: enabled === undefined ? apiOptions.enabled : apiOptions.enabled && enabled
        }
      ),
    undefined,
    options.computed_fields,
    forceRefetch
  );
};

export const useAllPeople = (
  page: number = 1,
  size: number = MAX_PEOPLE_PER_PAGE
): ExtendedUseQueryResult<PeoplePage> => {
  const result = useAPI("people/all", {
    queryParams: { page: page.toString(), size: size.toString() }
  });

  const addLead = useBoundStore((state) => state.people.addEntity);

  useEffect(() => {
    if (result.data?.people) {
      result.data.people.forEach(addLead);
    }
  }, [result.data, addLead]);

  return result;
};

export const useResetAllPeople = () => useResetAPI("people/all");

export interface DuplicatePerson {
  id: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  status: string | null;
}

export interface PersonDuplicateCheckResponse {
  person: DuplicatePerson | null;
}

export const useCheckPersonDuplicates = (
  email: string,
  enabled: boolean = true
): ExtendedUseQueryResult<PersonDuplicateCheckResponse> => {
  const trimmedEmail = email.trim();
  return useAPI(
    "people/check-duplicates",
    {
      method: "POST",
      data: { email: trimmedEmail }
    },
    { enabled: enabled && trimmedEmail.length > 0 }
  );
};

/** Resolves on 202, before the person exists — no store update; the outcome arrives as a Dreamer notification. */
export const useCreatePersonWithDreamerMutation = () =>
  useAPIMutation<{ email: string }, { email: string }>(
    "createPersonWithDreamer",
    { method: "POST" }
  );

export const useCreatePeopleMutation = () => {
  const addPerson = useBoundStore((state) => state.people.addEntity);
  return useAPIMutation<PersonData, APIObject>(
    "createPerson",
    {
      method: "POST"
    },
    (response, data) => {
      addPerson({
        ...data,
        id: response.id
      } as PersonData);
    }
  );
};

export const useUpdatePeopleMutation = () => {
  const updatePeople = useBoundStore((state) => state.people.updateEntity);
  const queryClient = useQueryClient();
  return useAPIMutation<
    { pathParams: { personId: string }; data: PersonData },
    PersonData
  >(
    "people/id",
    {
      method: "PUT"
    },
    (response, data) => {
      updatePeople(response.id, data.data as Partial<PersonData>);
      // Invalidate React Query cache so next fetch gets fresh data
      refreshEntityDetail(queryClient, "people", response.id);
    }
  );
};

export const usePeopleByCompany = (
  companyId: string
): ExtendedUseQueryResult<{ ids: string[] }> => {
  return useAPI(
    "people/company",
    {
      pathParams: { companyId: companyId }
    },
    { enabled: companyId !== "" }
  );
};

export const useUpdatePeopleInCompanyMutation = () => {
  const updatePeople = useBoundStore((state) => state.people.updateEntity);
  return useAPIMutation<
    {
      pathParams: {
        companyId: string;
      };
      data: {
        ids: string[];
      };
    },
    { ids: string[] }
  >(
    "people/updateCompany",
    {
      method: "POST"
    },
    (_response, args) => {
      const { companyId } = args.pathParams;
      args.data.ids.forEach((personId) => {
        updatePeople(personId, {
          companyId: companyId
        });
      });
    }
  );
};

export const {
  useFilteredEntitiesInfinite,
  useResetFilteredEntitiesInfinite: useResetPeopleInfinite
} = createUseFilteredEntitiesInfinite<PersonData>(
  "people",
  MAX_PEOPLE_PER_PAGE
);

export const usePeopleInfinite = (
  filters?: Filters,
  search?: SearchQuery,
  groupedBy?: keyof PersonData,
  pageSize = MAX_PEOPLE_PER_PAGE,
  sortField?: string,
  sortAsc?: boolean,
  fetchAll?: boolean,
  enabled = true,
  alwaysStale = false
) => {
  return useFilteredEntitiesInfinite(
    () => ({ addEntity: useBoundStore((state) => state.people.addEntity) }),
    {
      filters,
      search,
      groupedBy,
      pageSize,
      sortField,
      sortAsc,
      fetchAll,
      enabled,
      alwaysStale
    }
  );
};

export const useDeletePersonMutation = () => {
  const removePerson = useBoundStore((state) => state.people.removeEntity);
  return useAPIMutation<{ pathParams: { personId: string } }, void>(
    "people/id",
    {
      method: "DELETE"
    },
    (_response, data) => {
      removePerson(data.pathParams.personId);
    }
  );
};

export const usePeopleJobFunctions =
  (): ExtendedUseQueryResult<PersonJobFunctionsResponse> => {
    const storedJobFunctions = useBoundStore(
      (state) => state.lookup.data.personJobFunctions
    );
    const setPersonJobFunctions = useBoundStore(
      (state) => state.lookup.setPersonJobFunctions
    );

    const result = useAPI(
      "people/jobFunctions",
      {},
      {
        enabled: !storedJobFunctions
      }
    );

    useEffect(() => {
      if (
        result.isSuccess &&
        result.data?.jobFunctions &&
        !storedJobFunctions
      ) {
        setPersonJobFunctions(result.data.jobFunctions);
      }
    }, [
      result.data,
      result.isSuccess,
      setPersonJobFunctions,
      storedJobFunctions
    ]);

    if (storedJobFunctions) {
      return {
        ...result,
        data: { jobFunctions: storedJobFunctions },
        isSuccess: true,
        isLoading: false,
        isFetching: false
      } as ExtendedUseQueryResult<PersonJobFunctionsResponse>;
    }

    return result;
  };

export const useUpdatePeopleStatusMutation = () => {
  const updatePeople = useBoundStore((state) => state.people.updateEntity);
  return useAPIMutation<
    {
      pathParams: { personId: string; status: string };
      data: { reason: string | null };
    },
    PersonData
  >(
    "people/updateStatus",
    {
      method: "PUT"
    },
    (response, data) => {
      updatePeople(response.id, data.data as Partial<PersonData>);
    }
  );
};

export const usePeopleDistinctFilterOptions = (
  fieldName: string,
  filters?: Filters,
  searchTerm?: string
) => {
  return useDistinctFilterOptions("people", fieldName, filters, searchTerm);
};

export const usePersonByEmail = (
  email: string,
  enabled: boolean = true
): ExtendedUseQueryResult<PeoplePage> => {
  return useAPI("people/filter", {
    method: "POST",
    queryParams: { size: "1" },
    data: {
      filters: { email: { in_nocase: [email.trim()] } }
    }
  }, { enabled: enabled && email.trim().length > 0 });
};

export const usePersonEmailValidation = (
  email: string,
  enabled: boolean = true
) => {
  const trimmedEmail = email.trim();
  const filters: Filters = { email: { contains_nocase: trimmedEmail } };

  const infiniteResult = usePeopleInfinite(
    filters,
    undefined,
    undefined,
    1,
    undefined,
    undefined,
    false,
    enabled && trimmedEmail.length > 0,
    true
  );
  const data =
    infiniteResult.entities.length > 0 ? infiniteResult.entities[0] : null;

  return {
    ...infiniteResult,
    data
  };
};

export default {
  usePeople,
  useAllPeople,
  usePeopleInfinite,
  useResetPeopleInfinite
};
